import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ContactFragment, UpdateFinanceContactAddressDocument } from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { countries } from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface ContactAddressFormValues {
  id: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  postalCode?: string
  state?: string
  countryCode?: string
}

interface Props {
  contact: ContactFragment
  onSuccess?: () => void
  editable?: boolean
}

const ContactAddressForm: React.FC<Props> = ({ contact, onSuccess, editable = true }) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const { company } = useCurrentSession()

  const [updateContactAddress] = useMutation(UpdateFinanceContactAddressDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceContact,
  })

  const isAddressFilled = (data: ContactAddressFormValues): boolean => {
    const { addressLine1, addressLine2, city, postalCode, state } = data

    return !!(addressLine1 || addressLine2 || city || postalCode || state)
  }

  const { values, errors, setErrors, setValues, handleSubmit, isValid, dirty, touched } =
    useForm<ContactAddressFormValues>({
      enableReinitialize: true,

      defaultValues: {
        id: contact.id,
        addressLine1: contact.address?.line1 ?? '',
        addressLine2: contact.address?.line2 ?? undefined,
        city: contact.address?.city || '',
        postalCode: contact.address?.postalCode || '',
        state: contact.address?.state || '',
        countryCode: contact.address?.countryCode || '',
      },

      validate: (data) => {
        const errors: FormErrors<ContactAddressFormValues> = {}
        const addressFilled = isAddressFilled(data)

        if (addressFilled) {
          if (!data.addressLine1) {
            errors.addressLine1 = i18n.t('common.empty')
          }
          if (!data.city) {
            errors.city = i18n.t('common.empty')
          }
          if (!data.postalCode) {
            errors.postalCode = i18n.t('common.empty')
          }
          if (!data.state) {
            errors.state = i18n.t('common.empty')
          }
        }

        return errors
      },

      onSubmit: async (values) => {
        setSubmitting(true)

        const variables = {
          id: values.id,
          companyId: company.id,
          address: {
            line1: values.addressLine1 ?? '',
            line2: values.addressLine2,
            city: values.city ?? '',
            postalCode: values.postalCode ?? '',
            state: values.state ?? '',
            countryCode: values.countryCode ?? '',
          },
        }

        const result = await updateContactAddress({ variables })

        const handled = handleResult(result, (errors) => {
          if (errors) {
            setErrors(errors)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('finance.contacts.contact_form.update_contact.messages.error'),
            })
          }
        })

        setSubmitting(false)

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('finance.contacts.contact_form.update_contact.messages.success'),
          })

          onSuccess?.()
        }
        return handled
      },
    })

  const addressFilled = isAddressFilled(values)

  return (
    <>
      <Box gap="s24">
        <Select
          none={i18n.t('common.select')}
          value={values.countryCode?.toLowerCase()}
          label={i18n.t('finance.contacts.contact_form.fields.country')}
          options={countries(i18n.locale)}
          onChange={(value) => setValues({ ...values, countryCode: value })}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.address_line_1')}
          value={values.addressLine1}
          onChange={(value) => setValues({ ...values, addressLine1: value })}
          {...(addressFilled && { mandatory: true })}
          error={touched.addressLine1 ? errors.addressLine1 : undefined}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.address_line_2')}
          onChange={(value) => setValues({ ...values, addressLine2: value })}
          value={values.addressLine2}
          error={touched.addressLine2 ? errors.addressLine2 : undefined}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.city')}
          value={values.city}
          onChange={(value) => setValues({ ...values, city: value })}
          {...(addressFilled && { mandatory: true })}
          error={touched.city ? errors.city : undefined}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.zip_code')}
          value={values.postalCode}
          onChange={(value) => setValues({ ...values, postalCode: value })}
          {...(addressFilled && { mandatory: true })}
          error={touched.postalCode ? errors.postalCode : undefined}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.state_province_region')}
          value={values.state}
          onChange={(value) => setValues({ ...values, state: value })}
          {...(addressFilled && { mandatory: true })}
          error={touched.state ? errors.state : undefined}
          readonly={!editable}
        />
        {(!contact || dirty) && (
          <Box alignItems="flexEnd">
            <Button onClick={() => handleSubmit()} disabled={!editable || !isValid || submitting}>
              {i18n.t('common.submit')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}

export default ContactAddressForm
